import { useEffect, useMemo, useRef, useState } from 'react';
import { CheckCircle2, CircleDashed, Clock3, PlayCircle, RotateCw } from 'lucide-react';
import { motion, useInView } from 'motion/react';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { projectStages, type StageMediaType } from '../data/projectStages';

const statusStyles = {
  completed: {
    label: 'Completada',
    icon: CheckCircle2,
    badge: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  },
  active: {
    label: 'En ejecucion',
    icon: Clock3,
    badge: 'bg-blue-50 text-blue-700 border-blue-200',
  },
  planned: {
    label: 'Proximamente',
    icon: CircleDashed,
    badge: 'bg-slate-100 text-slate-700 border-slate-200',
  },
};

const mediaTypeLabels: Record<'all' | StageMediaType, string> = {
  all: 'Todo',
  photo: 'Fotos',
  video: 'Video',
  tour360: '360',
};

const ITEMS_PER_PAGE = 4;

function mediaTypeBadge(type: StageMediaType) {
  if (type === 'photo') return 'Foto';
  if (type === 'video') return 'Video';
  return '360';
}

export function Stages() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });

  const [activeStageId, setActiveStageId] = useState(projectStages[0]?.id ?? '');
  const [activeMediaType, setActiveMediaType] = useState<'all' | StageMediaType>('all');
  const [selectedMediaId, setSelectedMediaId] = useState(projectStages[0]?.media[0]?.id ?? '');
  const [currentPage, setCurrentPage] = useState(1);

  const activeStage = useMemo(
    () => projectStages.find((stage) => stage.id === activeStageId) ?? projectStages[0],
    [activeStageId],
  );

  const filteredMedia = useMemo(() => {
    if (!activeStage) return [];
    if (activeMediaType === 'all') return activeStage.media;
    return activeStage.media.filter((item) => item.type === activeMediaType);
  }, [activeStage, activeMediaType]);

  const pageCount = Math.max(1, Math.ceil(filteredMedia.length / ITEMS_PER_PAGE));
  const paginatedMedia = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredMedia.slice(start, start + ITEMS_PER_PAGE);
  }, [currentPage, filteredMedia]);

  const selectedMedia = useMemo(() => {
    if (!activeStage) return null;
    return (
      paginatedMedia.find((item) => item.id === selectedMediaId) ??
      paginatedMedia[0] ??
      filteredMedia[0] ??
      activeStage.media[0] ??
      null
    );
  }, [activeStage, filteredMedia, paginatedMedia, selectedMediaId]);

  useEffect(() => {
    if (!activeStage) return;
    setActiveMediaType('all');
    setCurrentPage(1);
    setSelectedMediaId(activeStage.media[0]?.id ?? '');
  }, [activeStageId, activeStage]);

  useEffect(() => {
    if (currentPage > pageCount) {
      setCurrentPage(1);
    }
  }, [currentPage, pageCount]);

  useEffect(() => {
    if (!selectedMedia && paginatedMedia.length > 0) {
      setSelectedMediaId(paginatedMedia[0].id);
    }
  }, [paginatedMedia, selectedMedia]);

  if (!activeStage) return null;

  const isUpcomingStage = activeStage.status === 'planned';
  const statusMeta = statusStyles[activeStage.status];
  const StatusIcon = statusMeta.icon;

  return (
    <section id="stages" className="py-20 md:py-28 bg-slate-50 border-t border-slate-200" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            className="inline-flex items-center px-4 py-2 rounded-full bg-white border border-slate-200 text-slate-700 mb-5 shadow-sm"
          >
            Plan Maestro
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 18 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.08 }}
            className="text-4xl md:text-5xl text-slate-950 mb-5 tracking-tight"
          >
            Desarrollo en 3 etapas
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15 }}
            className="text-lg text-slate-600 leading-relaxed"
          >
            Cada etapa tiene su propia narrativa audiovisual para seguimiento comercial y validacion tecnica.
          </motion.p>
        </div>

        <div className="grid gap-4 md:grid-cols-3 mb-8">
          {projectStages.map((stage, index) => {
            const isActive = stage.id === activeStage.id;
            const stageStatus = statusStyles[stage.status];
            const StageStatusIcon = stageStatus.icon;
            return (
              <motion.button
                key={stage.id}
                type="button"
                initial={{ opacity: 0, y: 16 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.08 + index * 0.08 }}
                onClick={() => setActiveStageId(stage.id)}
                className={`text-left rounded-2xl border px-5 py-5 transition-all ${
                  isActive
                    ? 'bg-slate-950 text-white border-slate-900 shadow-lg'
                    : 'bg-white text-slate-900 border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div
                    className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs uppercase tracking-[0.2em] ${
                      isActive ? 'bg-white/10 border-white/20 text-slate-200' : stageStatus.badge
                    }`}
                  >
                    <StageStatusIcon className="h-3.5 w-3.5" />
                    {stageStatus.label}
                  </div>
                  <span className={`text-sm ${isActive ? 'text-slate-300' : 'text-slate-500'}`}>{stage.period}</span>
                </div>
                <div className={`mt-4 text-xl font-semibold tracking-tight ${isActive ? 'text-white' : 'text-slate-950'}`}>
                  {stage.name}
                </div>
              </motion.button>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.25 }}
          className="rounded-3xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm"
        >
          <div className="grid gap-8 lg:grid-cols-[1.2fr_0.8fr]">
            <div>
              <div className="flex items-center justify-between gap-4 mb-4">
                <div>
                  <div className="text-sm text-slate-500">{activeStage.period}</div>
                  <h3 className="text-3xl text-slate-950 font-semibold tracking-tight">{activeStage.name}</h3>
                </div>
                <div
                  className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs uppercase tracking-[0.2em] ${statusMeta.badge}`}
                >
                  <StatusIcon className="h-3.5 w-3.5" />
                  {statusMeta.label}
                </div>
              </div>

              <p className="text-slate-600 leading-relaxed mb-5">{activeStage.description}</p>

              {isUpcomingStage ? (
                <div className="rounded-2xl border border-slate-200 bg-slate-100 p-8 md:p-10">
                  <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-3">Contenido multimedia</div>
                  <div className="text-3xl text-slate-950 font-semibold tracking-tight mb-3">Proximamente</div>
                  <p className="text-slate-600 leading-relaxed">
                    Esta etapa estara disponible con fotos, videos y recorrido 360 cuando inicie su ejecucion.
                  </p>
                </div>
              ) : (
                <>
                  <div className="flex flex-wrap gap-2 mb-5">
                    {(['all', 'photo', 'video', 'tour360'] as const).map((filterType) => (
                      <button
                        key={filterType}
                        type="button"
                        onClick={() => {
                          setActiveMediaType(filterType);
                          setCurrentPage(1);
                        }}
                        className={`rounded-full px-4 py-2 text-sm border transition-colors ${
                          activeMediaType === filterType
                            ? 'bg-slate-950 text-white border-slate-950'
                            : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
                        }`}
                      >
                        {mediaTypeLabels[filterType]}
                      </button>
                    ))}
                  </div>

                  <div className="rounded-2xl overflow-hidden border border-slate-200 bg-slate-100">
                    <div className="aspect-[16/9] relative">
                      {selectedMedia?.type === 'photo' && (
                        <ImageWithFallback src={selectedMedia.src} alt={selectedMedia.title} className="w-full h-full object-cover" />
                      )}
                      {selectedMedia?.type === 'video' && (
                        <iframe
                          className="w-full h-full"
                          src={selectedMedia.src}
                          title={selectedMedia.title}
                          loading="lazy"
                          sandbox="allow-scripts allow-same-origin allow-popups allow-presentation"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      )}
                      {selectedMedia?.type === 'tour360' && (
                        <iframe
                          className="w-full h-full"
                          src={selectedMedia.src}
                          title={selectedMedia.title}
                          loading="lazy"
                          sandbox="allow-scripts allow-same-origin allow-popups allow-presentation"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; xr-spatial-tracking"
                          allowFullScreen
                        />
                      )}

                      {selectedMedia && (
                        <div className="absolute left-4 top-4 rounded-full bg-slate-950/80 text-white px-3 py-1 text-xs uppercase tracking-[0.2em]">
                          {mediaTypeBadge(selectedMedia.type)}
                        </div>
                      )}
                    </div>
                  </div>

                  {selectedMedia && (
                    <div className="mt-4">
                      <div className="text-lg text-slate-950 font-semibold">{selectedMedia.title}</div>
                      <div className="text-slate-600">{selectedMedia.description}</div>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="space-y-4">
              <div>
                <h4 className="text-sm uppercase tracking-[0.2em] text-slate-500 mb-3">Entregables clave</h4>
                <div className="space-y-2">
                  {activeStage.deliverables.map((item) => (
                    <div key={item} className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                      {item}
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="text-sm uppercase tracking-[0.2em] text-slate-500 mb-3">Galeria interactiva</h4>
                {isUpcomingStage ? (
                  <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-5 text-sm text-slate-600">
                    Los archivos de esta etapa se publicaran proximamente.
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      {paginatedMedia.map((media) => {
                        const isSelected = selectedMedia?.id === media.id;
                        return (
                          <button
                            key={media.id}
                            type="button"
                            onClick={() => setSelectedMediaId(media.id)}
                            className={`group relative overflow-hidden rounded-xl border text-left transition-all ${
                              isSelected ? 'border-slate-950 ring-2 ring-slate-950/20' : 'border-slate-200 hover:border-slate-300'
                            }`}
                          >
                            <div className="aspect-[4/3]">
                              <ImageWithFallback src={media.thumbnail} alt={media.title} className="w-full h-full object-cover" />
                            </div>
                            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/75 to-transparent" />
                            <div className="absolute left-2 top-2 rounded-full bg-white/90 px-2 py-1 text-[11px] font-medium text-slate-800">
                              {mediaTypeBadge(media.type)}
                            </div>
                            <div className="absolute left-3 right-3 bottom-3 text-white text-sm font-medium leading-tight">
                              {media.title}
                            </div>
                            {media.type === 'video' && <PlayCircle className="absolute right-3 top-3 h-5 w-5 text-white/90" />}
                            {media.type === 'tour360' && <RotateCw className="absolute right-3 top-3 h-5 w-5 text-white/90" />}
                          </button>
                        );
                      })}
                    </div>

                    {pageCount > 1 && (
                      <div className="mt-4 flex items-center justify-between gap-3">
                        <button
                          type="button"
                          onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                          disabled={currentPage === 1}
                          className="rounded-full px-4 py-2 text-sm border border-slate-200 bg-white text-slate-700 disabled:opacity-45 disabled:cursor-not-allowed"
                        >
                          Anterior
                        </button>
                        <div className="text-sm text-slate-600">
                          Pagina {currentPage} de {pageCount}
                        </div>
                        <button
                          type="button"
                          onClick={() => setCurrentPage((prev) => Math.min(pageCount, prev + 1))}
                          disabled={currentPage === pageCount}
                          className="rounded-full px-4 py-2 text-sm border border-slate-200 bg-white text-slate-700 disabled:opacity-45 disabled:cursor-not-allowed"
                        >
                          Siguiente
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
